// ============================================================
// FILE: _services/ecg-standalone11/ecg-standalone11-frame-parser.ts
//
// Ported byte-for-byte from EcgConnectStandalone11's
// src/app/ecg/web-serial-ecg.service.ts (class EcgFrameParser). Protocol,
// confirmed against the vendor's own reference implementations:
//   Frame: SOF 0xAA (full, +3 status bytes) or 0xBB (short), 8 channels x
//   2 bytes big-endian, EOF 0x0A. Each channel: signed 16-bit, then <<1
//   (device's own fixed x2 software gain), wrapped back into signed 16-bit
//   to match the real hardware's own truncation.
//   500 Hz, 8 raw ADC channels/frame. 'y'/'x' ACK/NACK sent back per frame.
// ============================================================

export const ECG11_SOF_FULL = 0xaa;
export const ECG11_SOF_SHORT = 0xbb;
export const ECG11_EOF_BYTE = 0x0a;
export const ECG11_CH_NUM_RAW = 8;

const FULL_FRAME_LEN = 1 + 3 + ECG11_CH_NUM_RAW * 2 + 1;
const SHORT_FRAME_LEN = 1 + ECG11_CH_NUM_RAW * 2 + 1;

export interface Ecg11ParsedFrame {
  rawChannels: number[];
  status: { status1: number; status2: number; status3: number } | null;
}

/**
 * Self-resyncing byte-stream state machine -- fed by Web Serial's
 * ReadableStream chunks one Uint8Array at a time via feed(). Naturally
 * handles frames split across chunk boundaries.
 *
 * Buffers the current candidate frame (starting at its SOF byte) instead of
 * consuming bytes into fixed per-field slots, so that a candidate whose
 * end-of-frame check fails can be resynced without losing data: frames
 * arrive back to back, so the byte that failed the check is very often the
 * NEXT frame's SOF. Dropping just the false SOF and replaying everything
 * after it through the state machine (resync()) means the stream normally
 * re-locks within a frame or two, instead of hunting for the next SOF byte
 * in whatever follows -- which on a railed/constant signal (no leads
 * attached: the same bytes over and over, with 0xAA/0xBB/0x0A appearing
 * constantly in the noise) could stay misaligned far longer. NACKs are only
 * sent for the first failure of a damage event -- candidates that begin
 * inside a replay never NACK -- so one damaged frame yields one NACK, not a
 * burst.
 */
export class Ecg11FrameParser {
  // Bytes of the frame currently being assembled, starting at its SOF byte.
  private buf: number[] = [];
  private replaying = false;
  private candidateStartedInReplay = false;

  constructor(
    private onFrame: (frame: Ecg11ParsedFrame) => void,
    private onAckNeeded: (ok: boolean) => void
  ) {}

  feed(bytes: Uint8Array): void {
    for (const byte of bytes) this.feedByte(byte);
  }

  /** Clears any partially-assembled frame -- call after a transport-level gap (e.g. the read loop re-acquiring the reader after a non-fatal error) so bytes from before the gap can't be stitched onto bytes from after it. */
  reset(): void {
    this.buf.length = 0;
    this.replaying = false;
    this.candidateStartedInReplay = false;
  }

  private feedByte(byte: number): void {
    if (this.buf.length === 0) {
      if (byte === ECG11_SOF_FULL || byte === ECG11_SOF_SHORT) {
        this.buf.push(byte);
        this.candidateStartedInReplay = this.replaying;
      }
      return;
    }
    this.buf.push(byte);
    const need = this.buf[0] === ECG11_SOF_FULL ? FULL_FRAME_LEN : SHORT_FRAME_LEN;
    if (this.buf.length < need) return;

    if (this.buf[need - 1] === ECG11_EOF_BYTE) {
      const frame = this.emit();
      this.buf.length = 0;
      this.onAckNeeded(true);
      this.onFrame(frame);
      return;
    }
    if (!this.replaying && !this.candidateStartedInReplay) this.onAckNeeded(false);
    this.resync();
  }

  private resync(): void {
    const rest = this.buf.slice(1);
    this.buf.length = 0;
    const wasReplaying = this.replaying;
    this.replaying = true;
    for (const b of rest) this.feedByte(b);
    this.replaying = wasReplaying;
  }

  private emit(): Ecg11ParsedFrame {
    const hasStatus = this.buf[0] === ECG11_SOF_FULL;
    const chStart = hasStatus ? 4 : 1;
    const raw = new Array(ECG11_CH_NUM_RAW);
    for (let i = 0; i < ECG11_CH_NUM_RAW; i++) {
      const hi = this.buf[chStart + i * 2] & 0xff;
      const lo = this.buf[chStart + i * 2 + 1] & 0xff;
      // Combine big-endian, double (device's fixed x2 software gain), THEN
      // truncate to a signed 16-bit result so it wraps the same way the
      // real hardware's own firmware does.
      let value = ((hi << 8) | lo) << 1;
      value = (value << 16) >> 16;
      raw[i] = value;
    }
    const status = hasStatus
      ? { status1: this.buf[1], status2: this.buf[2], status3: this.buf[3] }
      : null;
    return { rawChannels: raw, status };
  }
}
